import type { DuplicateCompareDTO, DuplicateSideDTO, MergeFieldKey, MergeResultDTO, MergeSide } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitMerge } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { StageBadge } from '../../components/crm';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, Button, Card, CardBody, CardHeader, PageSpinner, useConfirm, useToast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { pluralize } from '../../lib/format';
import { confidenceColor } from './DuplicatesPage';

export function DuplicateComparePage() {
  const { id = '' } = useParams();
  const { data } = useQuery({
    queryKey: ['duplicates', id],
    queryFn: ({ signal }) => api.get<DuplicateCompareDTO>(`/api/duplicates/${id}`, { signal }),
  });
  if (!data) return <PageSpinner />;
  return <Compare key={`${data.id}:${data.status}`} pair={data} />;
}

function Compare({ pair }: { pair: DuplicateCompareDTO }) {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [primaryId, setPrimaryId] = useState(pair.suggestedPrimaryId);
  const [choices, setChoices] = useState<Partial<Record<MergeFieldKey, 'a' | 'b'>>>({});
  const primary = primaryId === pair.a.id ? pair.a : pair.b;
  const secondary = primaryId === pair.a.id ? pair.b : pair.a;
  const primarySide: 'a' | 'b' = primaryId === pair.a.id ? 'a' : 'b';
  const pending = pair.status === 'pending';

  /** Default per field: the kept record's value, unless it is empty and the other has one (same rule as the API). */
  const chosen = (key: MergeFieldKey): 'a' | 'b' => {
    const explicit = choices[key];
    if (explicit) return explicit;
    const field = pair.fields.find((f) => f.key === key)!;
    const kept = field[primarySide];
    const other = field[primarySide === 'a' ? 'b' : 'a'];
    return kept.empty && !other.empty ? (primarySide === 'a' ? 'b' : 'a') : primarySide;
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['duplicates'] });
  const merge = useMutation({
    mutationFn: () => {
      const fieldChoices: Partial<Record<MergeFieldKey, MergeSide>> = {};
      for (const field of pair.fields) fieldChoices[field.key] = chosen(field.key) === primarySide ? 'primary' : 'secondary';
      return api.post<MergeResultDTO>(`/api/duplicates/${pair.id}/merge`, { primaryId, fieldChoices });
    },
    onSuccess: async (result) => {
      toast.success(`Merged${result.contactsCombined ? ` — ${pluralize(result.contactsCombined, 'contact')} combined` : ''}`);
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      navigate(`/leads/${result.primaryId}`);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const resolve = useMutation({
    mutationFn: (action: 'keep-separate' | 'ignore' | 'reopen') => api.post(`/api/duplicates/${pair.id}/${action}`),
    onSuccess: async (_result, action) => {
      toast.success(action === 'keep-separate' ? 'Marked as different businesses' : action === 'ignore' ? 'Ignored' : 'Reopened for review');
      await refresh();
      if (action !== 'reopen') navigate('/duplicates');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const confirmMerge = async () => {
    const ok = await confirm({
      title: `Merge “${secondary.name}” into “${primary.name}”?`,
      message: `Its ${pluralize(secondary.counts.contacts, 'contact')}, ${pluralize(secondary.counts.activities, 'activity', 'activities')}, ${pluralize(secondary.counts.opportunities, 'deal')} and ${pluralize(secondary.counts.emails, 'email')} move to the kept record. A full snapshot is saved in the merge history.${secondary.hasLiveSequence && primary.hasLiveSequence ? ' Its automated sequence will be stopped (the kept record is already in one).' : ''}`,
      confirmLabel: 'Merge',
    });
    if (ok) merge.mutate();
  };

  return (
    <Page width="wide">
      <PageHeader
        title="Possible duplicate"
        breadcrumbs={[{ label: 'Duplicates', to: '/duplicates' }, { label: `${pair.a.name} / ${pair.b.name}` }]}
        meta={
          <>
            <Badge color={confidenceColor(pair.confidence)}>{pair.confidence}% match</Badge>
            {!pending && <Badge>{pair.status.replace('_', ' ')}</Badge>}
          </>
        }
        description={pair.signals.map((signal) => signal.detail).join(' · ')}
        actions={
          pending ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" loading={resolve.isPending} onClick={() => resolve.mutate('ignore')}>
                Ignore
              </Button>
              <Button loading={resolve.isPending} onClick={() => resolve.mutate('keep-separate')}>
                Different businesses
              </Button>
              {pair.canMerge && (
                <Button variant="primary" icon={GitMerge} loading={merge.isPending} onClick={() => void confirmMerge()}>
                  Merge
                </Button>
              )}
            </div>
          ) : pair.status !== 'merged' ? (
            <Button onClick={() => resolve.mutate('reopen')}>Reopen</Button>
          ) : undefined
        }
      />

      <Card className="mb-4">
        <CardHeader title="Fields" description={pending && pair.canMerge ? 'Pick which value to keep where they differ.' : undefined} />
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs text-slate-500">
                <th className="px-4 py-2 font-medium">Field</th>
                {(['a', 'b'] as const).map((key) => (
                  <th key={key} className="px-4 py-2 font-medium">
                    <label className="flex items-center gap-2">
                      {pending && pair.canMerge && (
                        <input type="radio" name="primary" checked={primaryId === pair[key].id} onChange={() => setPrimaryId(pair[key].id)} />
                      )}
                      <Link to={`/leads/${pair[key].id}`} className="text-slate-900 hover:text-ember-700">
                        {pair[key].name}
                      </Link>
                      {primaryId === pair[key].id && pending && <Badge color="green" size="sm">Keep this record</Badge>}
                    </label>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pair.fields.map((field) => (
                <tr key={field.key} className={field.differs ? 'border-b border-slate-50' : 'border-b border-slate-50 text-slate-400'}>
                  <td className="px-4 py-1.5 text-slate-500">{field.label}</td>
                  {(['a', 'b'] as const).map((sideKey) => (
                    <td key={sideKey} className="px-4 py-1.5">
                      <label className="flex items-center gap-2">
                        {pending && pair.canMerge && field.differs && (
                          <input
                            type="radio"
                            name={`field-${field.key}`}
                            checked={chosen(field.key) === sideKey}
                            onChange={() => setChoices({ ...choices, [field.key]: sideKey })}
                            disabled={field[sideKey].empty && !field[sideKey === 'a' ? 'b' : 'a'].empty}
                          />
                        )}
                        <span className={field[sideKey].empty ? 'text-slate-400' : undefined}>{field[sideKey].display ?? '—'}</span>
                      </label>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {[pair.a, pair.b].map((side) => (
          <SideCard key={side.id} side={side} kept={pending && side.id === primaryId} />
        ))}
      </div>
    </Page>
  );
}

function SideCard({ side, kept }: { side: DuplicateSideDTO; kept: boolean }) {
  return (
    <Card>
      <CardHeader
        title={side.name}
        description={`${side.counts.contacts} contacts · ${side.counts.activities} activities · ${side.counts.notes} notes · ${side.counts.tasks} tasks · ${side.counts.opportunities} deals · ${side.counts.emails} emails`}
        actions={<StageBadge stageId={side.pipelineStageId} />}
      />
      <CardBody className="space-y-3 text-[13px]">
        {!kept && <p className="text-xs text-slate-500">Everything below moves to the kept record on merge.</p>}
        <div>
          <p className="mb-1 text-xs font-medium text-slate-500">Contacts</p>
          {side.contacts.length === 0 ? (
            <p className="text-slate-400">None</p>
          ) : (
            <ul className="space-y-0.5">
              {side.contacts.map((contact) => (
                <li key={contact.id}>
                  {contact.name} {contact.title && <span className="text-slate-500">({contact.title})</span>} <span className="text-slate-500">{contact.email}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {side.opportunities.length > 0 && (
          <div>
            <p className="mb-1 text-xs font-medium text-slate-500">Deals</p>
            <ul>
              {side.opportunities.map((deal) => (
                <li key={deal.id}>
                  {deal.title} <span className="text-slate-500">({deal.stage})</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {side.recentActivities.length > 0 && (
          <div>
            <p className="mb-1 text-xs font-medium text-slate-500">Recent activity</p>
            <ul className="space-y-0.5 text-slate-600">
              {side.recentActivities.map((activity) => (
                <li key={activity.id}>{activity.summary}</li>
              ))}
            </ul>
          </div>
        )}
        {side.hasLiveSequence && <Badge color="amber">In an automated sequence</Badge>}
      </CardBody>
    </Card>
  );
}
